import Table from "@shared/Table";
import dateFormat from "dateformat";
import { useState, useEffect, useRef, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {PrinterIcon } from '@heroicons/react/24/outline'
import { useReactToPrint } from "react-to-print";
import useStallholders from "@hooks/useStallholders";
import { formatPrice } from "@shared/Utils";
import { StallholderPayment } from "@sharedtypes/StallholderPayment";
import StallholderSummary from "@components/StallholderSummary";
import { Stallholder } from "@sharedtypes/Stallholder";
import { stripTimeFromDate } from "@shared/Utils";

type PaymentReportProps = {
  payments: StallholderPayment[];
  endDate: Date;
};

const PaymentReport = ({payments, endDate }: PaymentReportProps) => {

  const stallholderContext = useStallholders();
  const [data, setData] = useState<StallholderPayment[]>([]);
  const [total, setTotal] = useState(0);
  const [printableReports, setPrintableReports] = useState<ReactNode[]>([]);

  const iPadPrintFix = `
    @page { margin: 12mm; }
    @media print {
      html, body {
        height: auto !important;
        overflow: visible !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      /* iPad Safari print killers */
      * {
        overflow: visible !important;
        -webkit-overflow-scrolling: auto !important;
        contain: none !important;
        transform: none !important;
        filter: none !important;
        backdrop-filter: none !important;
      }

      /* If you use sticky headers anywhere */
      .sticky, [style*="position:sticky"], [style*="position: fixed"] {
        position: static !important;
      }

      /* Reset off-screen positioning for print */
      [style*="left: -9999px"] {
        position: static !important;
        left: auto !important;
        top: auto !important;
        width: auto !important;
      }
    }
  `;

  const dealerReports = useRef<HTMLDivElement>(null);
  const printDealerReports = useReactToPrint({ contentRef: dealerReports, preserveAfterPrint: true, pageStyle: iPadPrintFix });

  const printable = useRef<HTMLInputElement>(null);
  const printSummaryReports = useReactToPrint({ contentRef: printable, preserveAfterPrint: true, pageStyle: iPadPrintFix });


  useEffect(() => {
    
    const enrichedPayments = payments.slice(0);
    const reports: ReactNode[] = [];
    const end = stripTimeFromDate(endDate);
    var newTotal = 0;

    enrichedPayments.forEach(function (item, index) {
      const stallholder = stallholderContext.findStallholderByID(item.id);
      if (stallholder) {
        newTotal += item.amount;
        item.name = stallholder.name;
        item.dealerID = stallholder.dealer_id;
        reports.push(<DealerReport startDate={item.start} endDate={end} stallholder={stallholder}/>)
      }

    });
    setTotal(newTotal);
    setData(enrichedPayments);
    setPrintableReports(reports);

    return () => {
    };
  }, [payments]); 

  const columns = [
    {
      "id": "dealerID",
      "label": "Dealer ID",
      "cellStyle": (row: number) => "px-6 py-1 font-medium text-lg text-gray-900 whitespace-nowrap dark:text-white dark:bg-gray-800",
      "headerStyle": "px-6 py-3 underline"
    },
    {
      "id": "name",
      "label": "Name",
      "cellStyle": (row: number) => "px-6 py-1 text-lg",
      "headerStyle": "px-6 py-4 underline"
    },
    {
      "id": "amount",
      "label": "Payment",
      "cellStyle": (row: number) => "px-6 py-1 text-lg",
      "headerStyle": "px-6 py-4 underline",
      "format": (cell: any, row: {}, index: number) => { 
        const price = cell as number;
        return formatPrice(price);
      }
    },
    {
      "id": "start",
      "label": "After",
      "cellStyle": (row: number) => "px-6 py-1 text-lg",
      "headerStyle": "px-6 py-4 underline"
    }
  ]

  const footer = <tfoot>
      <tr className="font-semibold text-gray-900 dark:text-white">
        <th></th>
        <th></th>
        <th scope="row" className="ml-10 border-t-2 border-b-2 border-zinc-600 px-6 text-base">{formatPrice(total)}</th>
      </tr>
    </tfoot>

  return (
    <>
      <div className="flex justify-end mb-12">

      <div>
        <button onClick={() => printDealerReports()}
                className="mr-6 inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <PrinterIcon className="w-6 h-6"/>
          Print dealer reports
        </button>
      </div>

      <div>
        <button onClick={() => printSummaryReports()}className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <PrinterIcon className="w-6 h-6"/>
          Print payments summary
        </button>
      </div>

      </div>
      <div>
        <h1 className="font-bold text-2xl flex justify-start mb-6">Payment report for close of business {dateFormat(endDate, "dddd, mmmm dS")}</h1>
        <Table data={data} columns={columns} footer={footer}/>
      </div>

      {createPortal(
        <div ref={printable}
             style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '210mm' }}>
          <h1 className="font-bold text-2xl flex justify-start mb-6">Payment report for close of business {dateFormat(endDate, "dddd, mmmm dS")}</h1>
          <Table data={data} columns={columns} footer={footer}/>
        </div>,
        document.body
      )}

      {createPortal(
        <div ref={dealerReports}
             style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '210mm' }}>
          {printableReports}
        </div>,
        document.body
      )}
    </>
  );
}

type DealerReportProps = {
  stallholder: Stallholder;
  endDate: string;
  startDate: string;
};

const DealerReport = ({stallholder, endDate, startDate }: DealerReportProps) => {

  return(
    <>
      <h1 className="text-2xl font-bold">Transaction summary from {dateFormat(startDate, "dS mmmm")} to {dateFormat(endDate, "dS mmmm yyyy")}</h1>
      <StallholderSummary start={stallholder.lastPaymentDateCalculation} end={endDate} stallholder={stallholder} showPrintButton={false} />
      <div className="pagebreak" />
    </>
  );
}

export default PaymentReport;
